import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { Input } from 'src/domains/layouts/shared/formComponents/input/Input';
import {
    NetDepositLimitState,
    DEFAULT_ROW,
    CALCULATION_TYPE_ARRAY,
    DepositLimitDashboardType,
    LIMIT_TYPE_ARRAY,
} from 'src/components/DepositLimitTab/NetDepositLimit/NetDepositLimit.state';
import { Button } from 'src/domains/layouts/shared/buttons/Button';
import { CurrencyType } from 'src_common/common/amount/website-money/currency';
import { CustomDropdown, FilterOptionType } from 'src/components/RealityCheckForm/CustomDropdown';
import { Switcher } from 'src/components/SwitcherNew/Switcher';
import {
    Heading,
    NetDepositLimitWrapper,
    NewLimitInfo,
    TableWrapper,
    NetDepositLimitEditRow,
    SwitcherWrapper,
    InputWrapper,
    EditRowWrapper,
    EmptyContainer,
    ButtonsWrapper,
    PendingRowWrapper,
    CustomDropdownWrapper,
    LimitTypeLockedHelperText,
    LimitTypeField,
} from 'src/components/DepositLimitTab/NetDepositLimit/NetDepositLimit.style';
import { getRollingLimits } from 'src_server/types/responsibleGambling';
import { DateTime } from 'src_common/utils/time/time';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { useCommon } from 'src/AppState/Common';
import { z } from 'zod';

const GROSS_LIMIT_TYPE_LOCKED_MESSAGE = 'Gross deposit requires Calendar type.';
interface DepositLimitFormPropsType {
    currency: CurrencyType;
    customerId: number;
    permissionDepositLimits: boolean;
    dashboard: DepositLimitDashboardType;
}

function calculationTypeLabel(value: string): string {
    const label = CALCULATION_TYPE_ARRAY.find((o) => o.value === value)?.label;
    const parsed = z.string().safeParse(label);
    return parsed.success ? parsed.data : value;
}

export const NetDepositLimit = observer(
    'NetDepositLimit',
    ({ currency, customerId, permissionDepositLimits, dashboard }: DepositLimitFormPropsType) => {
        const common = useCommon();
        const configState = ConfigState.get(common);
        const calculationTypeConfigurable = configState.config.depositLimitCalculationTypeConfigurable;
        const [state] = useState(
            () =>
                new NetDepositLimitState(
                    common.trpcClient,
                    () => customerId,
                    permissionDepositLimits,
                    configState,
                    dashboard
                )
        );

        return (
            <NetDepositLimitWrapper className='form__deposit-limit'>
                <div className='row'>
                    <div className='col-12'>
                        <Heading data-test='net-deposit-net-limit-heading'>
                            {dashboard === 'gross' ? 'Deposit Limits' : 'Net Deposit Limits'}
                        </Heading>
                    </div>
                </div>
                {DEFAULT_ROW === state.selectedEditRow && state.create_window_length_days_options_array.length !== 0 ? (
                    <EditRowWrapper
                        className='row'
                        reserveHelperSpace={state.isLimitTypeLockedToCalendar}
                    >
                        <div className='col-1'>
                            <NewLimitInfo>Set new:</NewLimitInfo>
                        </div>
                        <div className='col-2'>
                            <CustomDropdown
                                optionsList={(): Array<FilterOptionType<string>> =>
                                    state.create_window_length_days_options_array
                                }
                                selectedValue={(): string | null => state.selectedPeriod.value}
                                onChangeCallback={state.onUpdatePeriod}
                                placeholder='R. period'
                                labelHeader='R. period'
                                formInputState={state.selectedPeriod}
                                dataTest='dropdown-restriction-period'
                            />
                        </div>
                        <div className='col-2'>
                            {state.selectedPeriod.value === '0' ? (
                                'lifetime'
                            ) : (
                                <LimitTypeField>
                                    <CustomDropdown
                                        optionsList={(): Array<FilterOptionType<string>> => LIMIT_TYPE_ARRAY}
                                        selectedValue={(): string | null => state.limitType.value}
                                        onChangeCallback={state.onUpdateType}
                                        placeholder='Limit Type'
                                        labelHeader='Limit Type'
                                        formInputState={state.limitType}
                                        disabled={state.isLimitTypeLockedToCalendar}
                                        dataTest='dropdown-limit-type'
                                    />
                                    {state.isLimitTypeLockedToCalendar ? (
                                        <LimitTypeLockedHelperText data-test='limit-type-locked-message'>
                                            {GROSS_LIMIT_TYPE_LOCKED_MESSAGE}
                                        </LimitTypeLockedHelperText>
                                    ) : null}
                                </LimitTypeField>
                            )}
                        </div>
                        <div className='col-2'>
                            <InputWrapper>
                                <Input
                                    dataTest='net-deposit-net-limit-input'
                                    formInputState={state.limitValue}
                                    placeholder='Limit value'
                                />
                            </InputWrapper>
                        </div>
                        <div className='col-2'>
                            <InputWrapper>
                                <Input
                                    dataTest='net-deposit-net-limit-max-input'
                                    formInputState={state.limitValueMax}
                                    placeholder='Max limit value'
                                />
                            </InputWrapper>
                        </div>
                        {configState.config.depositLimitType === 'calendarType' ? (
                            <div className='col-2'></div>
                        ) : (
                            <div className='col-2'>
                                <SwitcherWrapper>
                                    <Switcher
                                        state={state.triggersWarningEmail}
                                        label='Send Email'
                                        dataTest='net-deposit-net-limit-send-email'
                                    />
                                </SwitcherWrapper>
                            </div>
                        )}
                        <div className='col-2'>
                            <SwitcherWrapper>
                                <Switcher
                                    state={state.isHardLimit}
                                    label='Hard Stop'
                                    dataTest='net-deposit-net-limit-hard-limit'
                                />
                            </SwitcherWrapper>
                        </div>
                        <div className='col-1'>
                            <Button
                                isDisabled={permissionDepositLimits}
                                dataTest='net-deposit-net-limit-set-limit-button'
                                onClick={state.onSubmit}
                            >
                                Set limit
                            </Button>
                        </div>
                    </EditRowWrapper>
                ) : (
                    <EmptyContainer>
                        {state.create_window_length_days_options_array.length > 0 ? '' : 'No limits to choose left'}
                    </EmptyContainer>
                )}
                <TableWrapper className='table'>
                    <thead>
                        <tr>
                            <th>Status</th>
                            <th>Duration in days</th>
                            {calculationTypeConfigurable ? <th>Calculation type</th> : null}
                            <th>Type</th>
                            <th>Limit value</th>
                            <th>Max limit value</th>
                            <th>Used so&nbsp;far</th>
                            <th>Remaining</th>
                            {configState.config.depositLimitType === 'calendarType' ? null : (
                                <>
                                    <th>Send email</th>
                                    <th>Email sent date</th>
                                </>
                            )}
                            <th>Hard stop</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody data-test='net-deposit-table-body'>
                        {[...state.getRollingLimits].map(([_days, data]) => {
                            if (data.windowLengthInDays === state.selectedEditRow) {
                                return (
                                    <React.Fragment key={data.windowLengthInDays}>
                                        <NetDepositLimitEditRow
                                            data-test={`net-deposit-net-limit-row-edit-${data.windowLengthInDays}`}
                                        >
                                            <td></td>
                                            <td data-test='net-deposit-net-limit-row-window-length'>
                                                {data.windowLengthInDays === 0 ? 'Lifetime' : data.windowLengthInDays}
                                            </td>
                                            {calculationTypeConfigurable ? (
                                                <td data-test='net-deposit-net-limit-row-calculation-type-value'>
                                                    {calculationTypeLabel(state.lockedCalculationType)}
                                                </td>
                                            ) : null}
                                            <CustomDropdownWrapper>
                                                {state.getLimitType(data.windowLengthInDays) === 'lifetime' ? (
                                                    'lifetime'
                                                ) : (
                                                    <>
                                                        <CustomDropdown
                                                            inlineMenu
                                                            optionsList={(): Array<FilterOptionType<string>> =>
                                                                LIMIT_TYPE_ARRAY
                                                            }
                                                            selectedValue={(): string | null => state.limitType.value}
                                                            onChangeCallback={state.onUpdateType}
                                                            placeholder='Limit Type'
                                                            labelHeader='Limit Type'
                                                            formInputState={state.limitType}
                                                            disabled={state.isLimitTypeLockedToCalendar}
                                                            dataTest='dropdown-inline-limit-type'
                                                        />
                                                        {state.isLimitTypeLockedToCalendar ? (
                                                            <LimitTypeLockedHelperText
                                                                inline
                                                                data-test='limit-type-locked-message-inline'
                                                            >
                                                                {GROSS_LIMIT_TYPE_LOCKED_MESSAGE}
                                                            </LimitTypeLockedHelperText>
                                                        ) : null}
                                                    </>
                                                )}
                                            </CustomDropdownWrapper>
                                            <td>
                                                <Input
                                                    dataTest='net-deposit-net-limit-row-input'
                                                    formInputState={state.limitValue}
                                                    placeholder='Net deposit limit'
                                                />
                                            </td>
                                            <td>
                                                <Input
                                                    dataTest='net-deposit-net-limit-row-max-input'
                                                    formInputState={state.limitValueMax}
                                                    placeholder='Net deposit limit'
                                                />
                                            </td>
                                            <td data-test='net-deposit-net-limit-used-value'>
                                                {state.usedLimit(
                                                    currency,
                                                    data.windowLengthInDays,
                                                    data.limitType,
                                                    data.calculationType
                                                )}
                                            </td>
                                            <td data-test='net-deposit-net-limit-remaining-value'>
                                                {state.remainingLimit(
                                                    currency,
                                                    data.windowLengthInDays,
                                                    data.activeLimitValue,
                                                    data.limitType,
                                                    data.calculationType
                                                )}
                                            </td>
                                            {configState.config.depositLimitType === 'calendarType' ? null : (
                                                <>
                                                    <td>
                                                        <Switcher
                                                            state={state.triggersWarningEmail}
                                                            label='Send Email'
                                                            dataTest='net-deposit-net-limit-row-send-email'
                                                        />
                                                    </td>

                                                    <td data-test='net-deposit-net-limit-row-send-email-date'>
                                                        {data.lastWarningEmailSentDate === null
                                                            ? 'n/a'
                                                            : DateTime.from(data.lastWarningEmailSentDate)?.format(
                                                                  'YYYY-MM-DD HH:mm'
                                                              )}
                                                    </td>
                                                </>
                                            )}
                                            <td>
                                                <Switcher
                                                    state={state.isHardLimit}
                                                    label='Hard Limit'
                                                    dataTest='net-deposit-net-limit-row-hard-limit'
                                                />
                                            </td>
                                            <ButtonsWrapper>
                                                <Button
                                                    isDisabled={permissionDepositLimits}
                                                    dataTest='net-deposit-net-limit-row-cancel-limit-button'
                                                    onClick={state.onCancel}
                                                    size='small'
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    isDisabled={permissionDepositLimits}
                                                    dataTest='net-deposit-net-limit-row-set-limit-button'
                                                    onClick={state.onSubmit}
                                                    size='small'
                                                >
                                                    Submit
                                                </Button>
                                                <Button
                                                    isDisabled={permissionDepositLimits}
                                                    dataTest='net-deposit-net-limit-row-delete-limit-button'
                                                    onClick={state.onDelete}
                                                    size='small'
                                                >
                                                    Delete
                                                </Button>
                                            </ButtonsWrapper>
                                        </NetDepositLimitEditRow>
                                        <NetDepositLimitRow
                                            calculationTypeConfigurable={calculationTypeConfigurable}
                                            pendingLimitValue={data.pendingLimitValue}
                                            pendingRequestedAt={data.pendingRequestedAt}
                                            currency={currency}
                                            state={state}
                                            windowLengthInDays={data.windowLengthInDays}
                                        />
                                    </React.Fragment>
                                );
                            } else {
                                return (
                                    <React.Fragment key={data.windowLengthInDays}>
                                        <tr data-test={`net-deposit-net-limit-row-${data.windowLengthInDays}`}>
                                            <td>Active</td>
                                            <td data-test='net-deposit-net-limit-row-window-length-value'>
                                                {data.windowLengthInDays === 0 ? 'Lifetime' : data.windowLengthInDays}
                                            </td>
                                            {calculationTypeConfigurable ? (
                                                <td data-test='net-deposit-net-limit-row-calculation-type-value'>
                                                    {calculationTypeLabel(
                                                        state.getCalculationTypeForRow(data.windowLengthInDays)
                                                    )}
                                                </td>
                                            ) : null}
                                            <td data-test='net-deposit-net-limit-row-limit-type-value'>
                                                {state.getLimitType(data.windowLengthInDays) ?? 'n/a'}
                                            </td>
                                            <td data-test='net-deposit-net-limit-row-limit-value'>
                                                {state.renderAmount(currency, data.activeLimitValue)}
                                            </td>
                                            <td data-test='net-deposit-net-limit-row-limit-max-value'>
                                                {data.limitValueMax === null
                                                    ? 'not set'
                                                    : state.renderAmount(currency, data.limitValueMax)}
                                            </td>
                                            <td data-test='net-deposit-net-limit-used-value'>
                                                {state.usedLimit(
                                                    currency,
                                                    data.windowLengthInDays,
                                                    data.limitType,
                                                    data.calculationType
                                                )}
                                            </td>
                                            <td data-test='net-deposit-net-limit-remaining-value'>
                                                {state.remainingLimit(
                                                    currency,
                                                    data.windowLengthInDays,
                                                    data.activeLimitValue,
                                                    data.limitType,
                                                    data.calculationType
                                                )}
                                            </td>
                                            {configState.config.depositLimitType === 'calendarType' ? null : (
                                                <>
                                                    <td data-test='net-deposit-net-limit-row-send-email-value'>
                                                        {data.triggersWarningEmail.toString()}
                                                    </td>
                                                    <td data-test='net-deposit-net-limit-row-send-email-date-value'>
                                                        {data.lastWarningEmailSentDate ?? 'n/a'}
                                                    </td>
                                                </>
                                            )}
                                            <td data-test='net-deposit-net-limit-row-hard-limit-value'>
                                                {data.hardLimit.toString()}
                                            </td>
                                            <td>
                                                <Button
                                                    isDisabled={permissionDepositLimits}
                                                    dataTest='net-deposit-net-limit-row-edit-limit-button'
                                                    onClick={() => state.onEditRow(data.windowLengthInDays)}
                                                >
                                                    Edit
                                                </Button>
                                            </td>
                                        </tr>
                                        <NetDepositLimitRow
                                            calculationTypeConfigurable={calculationTypeConfigurable}
                                            pendingLimitValue={data.pendingLimitValue}
                                            pendingRequestedAt={data.pendingRequestedAt}
                                            currency={currency}
                                            state={state}
                                            windowLengthInDays={data.windowLengthInDays}
                                        />
                                    </React.Fragment>
                                );
                            }
                        })}
                    </tbody>
                </TableWrapper>
            </NetDepositLimitWrapper>
        );
    }
);

interface NetDepositLimitRowPropsType {
    calculationTypeConfigurable: boolean;
    pendingLimitValue: getRollingLimits.LimitsObjectSchemaType['pendingLimitValue'];
    pendingRequestedAt: getRollingLimits.LimitsObjectSchemaType['pendingRequestedAt'];
    currency: CurrencyType;
    state: NetDepositLimitState;
    windowLengthInDays: number;
}
export const NetDepositLimitRow = observer(
    'NetDepositLimitRow',
    ({
        calculationTypeConfigurable,
        pendingLimitValue,
        pendingRequestedAt,
        currency,
        state,
        windowLengthInDays,
    }: NetDepositLimitRowPropsType) => {
        if (pendingLimitValue === null || pendingRequestedAt === null) {
            return (
                <PendingRowWrapper
                    key={`pending-${windowLengthInDays}`}
                    data-test={`net-deposit-net-limit-row-pending-${windowLengthInDays}`}
                >
                    {[1, 7, 30].includes(windowLengthInDays) ? (
                        <>
                            <td>Pending: </td>
                            <td>--</td>
                            <td></td>
                        </>
                    ) : null}
                </PendingRowWrapper>
            );
        }
        return (
            <PendingRowWrapper
                key={windowLengthInDays}
                data-test={`net-deposit-net-limit-row-pending-${windowLengthInDays}`}
            >
                <td>Pending until</td>
                <td data-test='net-deposit-net-limit-pendingRequestedAt'>
                    {DateTime.from(pendingRequestedAt)?.addDays(1).format('YYYY-MM-DD HH:mm')}
                </td>
                {calculationTypeConfigurable ? <td></td> : null}
                <td></td>
                <td data-test='net-deposit-net-limit-pending-limit-value'>
                    {state.renderAmount(currency, pendingLimitValue)}
                </td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
            </PendingRowWrapper>
        );
    }
);
